import {createPortal} from 'react-dom';
import {useIntl} from 'react-intl';
import {Loader2, Phone, Video} from 'lucide-react';
import {useRoomCallSummary} from '../../groupCalls/hooks/useGroupCall';
import {countCallPeople} from '../../groupCalls/services/callPeople';
import type {CallSwitch, IncomingGroupCall} from '../../groupCalls/services/groupCallOrchestrator';
import {CallAvatar} from '../components/CallAvatar';
import {CallDialog, DialogButton} from '../components/CallDialog';
import {groupSurfaceMessages as m} from './groupMessages';
import {DockedCard} from '../components/DockedCard';
import {CallLayer} from '../components/CallLayer';

interface InviteBannerProps {
    readonly invite: IncomingGroupCall;
    readonly variant: 'stage' | 'bubble';
    readonly onDecline: () => void;
    readonly onLeaveAndJoin: () => void;
}

const useInviteDetails = (invite: IncomingGroupCall): string => {
    const intl = useIntl();
    const summary = useRoomCallSummary(invite.roomId);
    const count = summary ? countCallPeople(summary.userIds) : 0;
    return count > 0
        ? intl.formatMessage(m.inviteDetailsCount, {room: invite.roomName, intent: invite.intent, count})
        : intl.formatMessage(m.inviteDetails, {room: invite.roomName, intent: invite.intent});
};

export const InviteBanner = ({invite, variant, onDecline, onLeaveAndJoin}: InviteBannerProps) => {
    const intl = useIntl();
    const details = useInviteDetails(invite);
    const Icon = invite.intent === 'video' ? Video : Phone;
    const stage = variant === 'stage';
    const buttons = (
        <div className={`flex gap-2 ${stage ? '' : 'w-full'}`}>
            <button
                type="button"
                data-testid="group-call-invite-decline"
                onClick={onDecline}
                className={`phq-call-press inline-flex h-9 items-center justify-center whitespace-nowrap rounded-[10px] bg-[var(--phq-call-surface-muted)] px-3.5 text-sm font-semibold ${stage ? '' : 'flex-1'}`}
            >
                {intl.formatMessage(m.inviteDecline)}
            </button>
            <button
                type="button"
                data-testid="group-call-invite-accept"
                onClick={onLeaveAndJoin}
                className={`phq-call-press inline-flex h-9 items-center justify-center whitespace-nowrap rounded-[10px] bg-[var(--phq-call-brand)] px-3.5 text-sm font-semibold text-white ${stage ? '' : 'flex-1'}`}
            >
                {intl.formatMessage(m.inviteLeaveAndJoin)}
            </button>
        </div>
    );
    const content = (
        <>
            <div className={`flex min-w-0 items-center gap-3 ${stage ? 'min-w-[180px] flex-1' : ''}`}>
                <CallAvatar identity={{name: invite.callerName, userId: invite.callerUserId}} size={stage ? 40 : 32}/>
                <div className="min-w-0 flex-1">
                    <p className="truncate text-[15px] font-semibold">{intl.formatMessage(m.inviteTitle, {name: invite.callerName})}</p>
                    <p className="flex min-w-0 items-center gap-1.5 text-[13px] text-[var(--phq-call-ink-muted)]">
                        <Icon size={14} className="shrink-0" aria-hidden="true"/>
                        <span className="truncate">{details}</span>
                    </p>
                </div>
            </div>
            {buttons}
        </>
    );
    if (!stage) {
        return (
            <div
                role="region"
                aria-label={intl.formatMessage(m.inviteRegion)}
                data-testid="group-call-invite"
                className="phq-call-swap flex flex-col gap-3 rounded-xl bg-[var(--phq-call-surface)] p-3 shadow-[inset_0_0_0_1px_var(--phq-call-line-strong)]"
            >
                {content}
            </div>
        );
    }
    return (
        <div className="absolute left-1/2 top-[60px] z-[6] w-[600px] max-w-[calc(100%-32px)] -translate-x-1/2">
            <div
                role="region"
                aria-label={intl.formatMessage(m.inviteRegion)}
                data-testid="group-call-invite"
                className="phq-call-enter flex flex-wrap items-center gap-3 rounded-2xl bg-[var(--phq-call-surface)] py-3 pl-3.5 pr-3 phq-call-raised"
            >
                {content}
            </div>
        </div>
    );
};

export const InviteConfirmDialog = ({invite, onCancel, onConfirm}: {invite: IncomingGroupCall; onCancel: () => void; onConfirm: () => void}) => {
    const intl = useIntl();
    return (
        <CallDialog
            role="alertdialog"
            width="sm"
            title={intl.formatMessage(m.inviteConfirmTitle)}
            testId="group-call-invite-confirm"
            onClose={onCancel}
            footer={(
                <>
                    <DialogButton tone="neutral" label={intl.formatMessage(m.cancel)} onClick={onCancel}/>
                    <DialogButton tone="brand" label={intl.formatMessage(m.inviteLeaveAndJoin)} testId="group-call-invite-confirm-accept" onClick={onConfirm}/>
                </>
            )}
        >
            <p className="text-sm leading-[1.5] text-[var(--phq-call-ink-muted)]">
                {intl.formatMessage(m.inviteConfirmBody, {room: invite.roomName, name: invite.callerName})}
            </p>
        </CallDialog>
    );
};

export const SwitchingOverlay = ({switching}: {switching: CallSwitch}) => {
    const intl = useIntl();
    const body = (
        <>
            <Loader2 size={48} className="animate-spin text-[var(--phq-call-brand-focus)]" aria-hidden="true"/>
            <p className="text-lg font-semibold">{intl.formatMessage(m.switchingTitle)}</p>
            <p className="max-w-[420px] text-center text-sm text-[var(--phq-call-ink-muted)]">
                {intl.formatMessage(m.switchingBody, {from: switching.from, to: switching.to})}
            </p>
        </>
    );
    return createPortal(
        <CallLayer>{
        switching.expanded ? (
            <div
                role="status"
                data-testid="group-call-switching"
                className="phq-call-ui fixed bottom-0 left-14 right-0 top-0 z-[90] flex flex-col items-center justify-center gap-4 bg-[var(--phq-call-canvas)] text-[var(--phq-call-ink)] [background-image:var(--phq-call-stage-gradient)]"
            >
                {body}
            </div>
        ) : (
            <DockedCard
                width={320}
                role="status"
                testId="group-call-switching"
                className="phq-call-ui z-[90] flex flex-col items-center gap-3 rounded-[24px] bg-[var(--phq-call-canvas)] px-4 py-6 text-[var(--phq-call-ink)] phq-call-shadow-overlay [background-image:var(--phq-call-stage-gradient)]"
            >
                {body}
            </DockedCard>
        )}</CallLayer>,
        document.body,
    );
};
